/**
 * Auswertung des Stripe-Connect-Accounts eines Gastgebers für die UI.
 *
 * Der Unterschied, auf den es ankommt: kann der Account gerade Geld annehmen
 * oder nicht? Ist die `transfers`-Capability nicht aktiv, lehnt der Guard in
 * `server/api/initiate-privileged.js` jede Buchung auf die Inserate dieses
 * Gastgebers ab - er sieht davon aber nichts außer ausbleibenden Anfragen.
 *
 * Zwei Fälle, die für den Gastgeber völlig verschieden sind:
 *  - `blocking`: Stripe wartet auf ihn (fehlende Angaben/Dokumente). Er muss handeln.
 *  - `pending`:  Stripe prüft noch. Er kann nichts tun außer warten.
 */

export const STRIPE_ISSUE_BLOCKING = 'blocking';
export const STRIPE_ISSUE_PENDING = 'pending';

// Stripe-Requirement-Keys sind fein granuliert (`individual.address.city`,
// `individual.address.line1`, ...). Für den Gastgeber zählt die Kategorie, nicht
// das einzelne Feld - sonst steht da eine Liste aus neun Zeilen für "Adresse und
// Geburtsdatum fehlen".
const REQUIREMENT_CATEGORIES = [
  { id: 'document', test: /verification\.(additional_)?document/ },
  { id: 'address', test: /\.address[._]/ },
  { id: 'birthday', test: /\.dob\./ },
  { id: 'name', test: /\.(first_name|last_name|name)$/ },
  { id: 'phone', test: /\.phone$/ },
  { id: 'email', test: /\.email$/ },
  { id: 'idNumber', test: /(id_number|ssn_last_4)/ },
  { id: 'bankAccount', test: /^external_account/ },
  { id: 'business', test: /^(company|business_profile|business_type)/ },
];

const OTHER_CATEGORY = 'other';

/**
 * Requirement-Keys zu Kategorien zusammenfassen, Reihenfolge stabil.
 *
 * @param {string[]} requirementKeys
 * @returns {string[]} Kategorie-IDs, ohne Dopplungen
 */
export const categorizeStripeRequirements = (requirementKeys) => {
  const categories = [];
  (requirementKeys || []).forEach((key) => {
    const match = REQUIREMENT_CATEGORIES.find((category) => category.test.test(key));
    const id = match ? match.id : OTHER_CATEGORY;
    if (!categories.includes(id)) {
      categories.push(id);
    }
  });
  return categories;
};

/**
 * Was ist mit diesem Account los?
 *
 * @param {object|null} stripeAccountData Das rohe Stripe-Account-Objekt aus
 *   `stripeAccount.attributes.stripeAccountData`.
 * @returns {{ severity: string, requirementCategories: string[], disabledReason: string|null }|null}
 *   null, wenn alles in Ordnung ist (oder keine Daten vorliegen).
 */
export const getStripeAccountIssue = (stripeAccountData) => {
  if (!stripeAccountData) {
    return null;
  }

  const requirements = stripeAccountData.requirements || {};
  const capabilities = stripeAccountData.capabilities || {};

  const canReceiveTransfers = capabilities.transfers === 'active';
  const chargesDisabled = stripeAccountData.charges_enabled === false;
  const payoutsDisabled = stripeAccountData.payouts_enabled === false;

  if (canReceiveTransfers && !chargesDisabled && !payoutsDisabled) {
    return null;
  }

  const openRequirements = [
    ...(requirements.past_due || []),
    ...(requirements.currently_due || []),
  ];
  const requirementCategories = categorizeStripeRequirements(openRequirements);
  const disabledReason = requirements.disabled_reason || null;

  // Nichts offen und trotzdem keine Transfers: Stripe prüft noch. Den Gastgeber
  // in dem Fall zum Nachreichen aufzufordern wäre schlicht falsch.
  const waitingOnStripe =
    openRequirements.length === 0 &&
    (disabledReason === 'requirements.pending_verification' ||
      (requirements.pending_verification || []).length > 0 ||
      !disabledReason);

  return {
    severity: waitingOnStripe ? STRIPE_ISSUE_PENDING : STRIPE_ISSUE_BLOCKING,
    requirementCategories,
    disabledReason,
  };
};

/**
 * Nimmt dieser Account gerade keine Buchungen an? Genau die Frage, die der
 * Server-Guard stellt - dieselbe Bedingung, damit UI und Backend nicht
 * auseinanderlaufen.
 *
 * @param {object|null} stripeAccountData
 * @returns {boolean}
 */
export const cannotReceiveBookings = (stripeAccountData) =>
  !!stripeAccountData && stripeAccountData.capabilities?.transfers !== 'active';
